import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
// Contenedor donde vive el dialogo — el <main> del AppLayout (id="app-main")
// para que ocupe solo el área de contenido, dejando sidebar y topbar visibles.
function getDialogHost(): HTMLElement {
  return document.getElementById('app-main') ?? document.body
}
import { currentLocaleTag } from '../lib/formatting'
import { useAuth } from '../contexts/AuthContext'
import { confirmDialog } from '../lib/dialogs'
import {
  AlertCircle,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  FileText,
  Loader2,
  ScanEye,
  ScanText,
  Search,
  Sparkles,
  Upload,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import ColumnSortTh from '../components/ui/ColumnSortTh'
import {
  type AnalyzeInvoiceResponse,
  type DocumentExtractedResponse,
  type InvoiceMeta,
  type ReceivedInvoiceItem,
  type ReceivedInvoicesResponse,
} from '@jr2os/shared'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../lib/money'

const PAGE_SIZE = 50

export default function OCR() {
  const { t } = useTranslation(['documents', 'common'])
  const [searchParams, setSearchParams] = useSearchParams()
  const q = searchParams.get('q') ?? ''
  const year = searchParams.get('year') ?? ''
  const month = searchParams.get('month') ?? ''
  const status = searchParams.get('status') ?? ''
  const sort = searchParams.get('sort') ?? ''
  const dir: 'asc' | 'desc' = searchParams.get('dir') === 'desc' ? 'desc' : 'asc'
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)

  const [qInput, setQInput] = useState(q)
  const [data, setData] = useState<ReceivedInvoicesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [viewer, setViewer] = useState<ReceivedInvoiceItem | null>(null)
  /** Factura ya subida cuyos campos se están corrigiendo. */
  const [editDoc, setEditDoc] = useState<ReceivedInvoiceItem | null>(null)
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const onDelete = async (ri: ReceivedInvoiceItem) => {
    if (
      !(await confirmDialog({
        message: t('ocr.list.deleteConfirm', { name: ri.invoiceMeta?.supplier || ri.fileName }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/documents/${ri.id}`)
      await load()
    } catch (e) {
      setError(extractErr(e) || t('ocr.list.deleteFailed'))
    }
  }

  const updateParam = useCallback(
    (k: string, v: string) => {
      const next = new URLSearchParams(searchParams)
      if (v) next.set(k, v)
      else next.delete(k)
      if (k !== 'page') next.delete('page')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Debounce del buscador → URL (evita recargar en cada tecla).
  useEffect(() => {
    const id = setTimeout(() => {
      if (qInput !== q) updateParam('q', qInput)
    }, 350)
    return () => clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (q) params.set('q', q)
      if (year) params.set('year', year)
      if (month) params.set('month', month)
      if (status) params.set('status', status)
      if (sort) {
        params.set('sort', sort)
        params.set('dir', dir)
      }
      params.set('page', String(page))
      params.set('pageSize', String(PAGE_SIZE))
      const res = await api.get<ReceivedInvoicesResponse>(
        `/documents/received-invoices?${params.toString()}`
      )
      setData(res)
    } catch (err) {
      setError(extractErr(err))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [q, year, month, status, sort, dir, page])

  useEffect(() => {
    void load()
  }, [load])

  const onDownload = useCallback(
    async (item: ReceivedInvoiceItem) => {
      if (!item.latestVersion) {
        setError(t('ocr.list.noDocument'))
        return
      }
      // Visor directo vía API con disposición inline (el PDF se abre en el
      // navegador); las URLs prefirmadas de MinIO no son resolubles fuera de Docker.
      window.open(
        `/api/documents/${item.id}/versions/${item.latestVersion}/download?inline=1`,
        '_blank'
      )
    },
    [t]
  )

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(total, page * PAGE_SIZE)
  const hasFilters = Boolean(q || year || month || status)
  const tag = currentLocaleTag()

  /** Click en una cabecera: sin orden → ascendente → descendente → sin orden (el de siempre: lo último subido primero). */
  const onSort = (key: string) => {
    const next = new URLSearchParams(searchParams)
    next.delete('page')
    if (sort !== key) {
      next.set('sort', key)
      next.set('dir', 'asc')
    } else if (dir === 'asc') {
      next.set('dir', 'desc')
    } else {
      next.delete('sort')
      next.delete('dir')
    }
    setSearchParams(next, { replace: true })
  }
  const th = (key: string, label: string, align: 'left' | 'right' | 'center') => (
    <ColumnSortTh
      sortKey={key}
      label={label}
      align={align}
      active={sort === key}
      dir={dir}
      onSort={onSort}
      ariaLabel={t('ocr.list.sortBy', { col: label })}
    />
  )

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <ScanText size={20} className="text-jr2-700 dark:text-jr2-300" />
            {t('ocr.title')}
          </h1>
          <p className="text-xs t-muted mt-0.5">{t('ocr.listSubtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5"
        >
          <Upload size={14} /> {t('ocr.uploadInvoice')}
        </button>
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 t-faint pointer-events-none" />
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={t('ocr.list.searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-jr2-500/50"
          />
        </div>
        <FilterSelect
          label={t('ocr.list.year')}
          value={year}
          onChange={(v) => updateParam('year', v)}
          options={[
            { v: '', l: t('ocr.list.allYears') },
            ...(data?.years ?? []).map((y) => ({ v: String(y), l: String(y) })),
          ]}
        />
        <FilterSelect
          label={t('ocr.list.month')}
          value={month}
          onChange={(v) => updateParam('month', v)}
          options={[
            { v: '', l: t('ocr.list.allMonths') },
            ...Array.from({ length: 12 }, (_, m) => ({
              v: String(m + 1),
              l: new Date(Date.UTC(2000, m, 1)).toLocaleDateString(tag, {
                month: 'long',
                timeZone: 'UTC',
              }),
            })),
          ]}
        />
        <FilterSelect
          label={t('ocr.list.status')}
          value={status}
          onChange={(v) => updateParam('status', v)}
          options={[
            { v: '', l: t('ocr.list.allStatuses') },
            { v: 'PENDIENTE', l: t('ocr.list.expStatus.PENDIENTE') },
            { v: 'PAGADO', l: t('ocr.list.expStatus.PAGADO') },
            { v: 'ANULADO', l: t('ocr.list.expStatus.ANULADO') },
            { v: 'SIN_GASTO', l: t('ocr.list.expStatus.SIN_GASTO') },
          ]}
        />
        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              // Limpia los filtros pero conserva el orden elegido.
              const keep = new URLSearchParams()
              if (sort) {
                keep.set('sort', sort)
                keep.set('dir', dir)
              }
              setSearchParams(keep, { replace: true })
            }}
            className="text-xs t-muted hover:t-primary underline"
          >
            {t('ocr.list.clearFilters')}
          </button>
        )}
      </div>

      {error && (
        <div className="text-sm rounded-lg px-3 py-2 border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* Tabla */}
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-muted">
            <tr>
              {th('supplier', t('ocr.list.cols.supplier'), 'left')}
              {th('invoiceNumber', t('ocr.list.cols.invoiceNumber'), 'left')}
              {th('date', t('ocr.list.cols.date'), 'left')}
              {th('base', t('ocr.list.cols.base'), 'right')}
              {th('vat', t('ocr.list.cols.vat'), 'right')}
              {th('irpf', t('ocr.list.cols.irpf'), 'right')}
              {th('total', t('ocr.list.cols.total'), 'right')}
              {th('expense', t('ocr.list.cols.expense'), 'left')}
              {th('source', t('ocr.list.cols.source'), 'center')}
              <th className="px-3 py-2 text-right">{t('ocr.list.cols.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin mr-2" /> {t('ocr.loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-12 text-center">
                  <ScanText size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {hasFilters ? t('ocr.list.noResults') : t('ocr.list.empty')}
                  </p>
                </td>
              </tr>
            )}
            {!loading &&
              items.map((ri) => {
                const m = ri.invoiceMeta
                const supplier = m?.supplier || ri.fileName
                const dateStr = m?.issueDate ?? ri.createdAt
                return (
                  <tr key={ri.id} className="border-t border-base/40 hover:bg-hover">
                    <td className="px-3 py-2 t-primary max-w-[220px]">
                      <div className="flex items-center gap-1.5">
                        <FileText size={13} className="t-faint shrink-0" />
                        <span className="truncate" title={ri.fileName}>{supplier}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs t-secondary">{m?.invoiceNumber ?? '—'}</td>
                    <td className="px-3 py-2 t-secondary whitespace-nowrap">
                      {dateStr ? new Date(dateStr).toLocaleDateString(currentLocaleTag()) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right t-secondary font-mono">
                      {m?.baseCents != null ? formatCents(m.baseCents) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right t-faint font-mono">
                      {m?.vatCents != null ? formatCents(m.vatCents) : '—'}
                    </td>
                    {/* Manda la retención del GASTO confirmado sobre la que
                        adivinó el OCR: es la que de verdad se ha contabilizado. */}
                    <td className="px-3 py-2 text-right font-mono text-xs">
                      {(() => {
                        const irpf = ri.expense?.irpfCents ?? m?.irpfCents ?? null
                        if (irpf == null) return <span className="t-faint">—</span>
                        if (irpf === 0) return <span className="t-faint">—</span>
                        return (
                          <span className="text-amber-700 dark:text-amber-300">
                            −{formatCents(irpf)}
                          </span>
                        )
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right t-primary font-semibold font-mono">
                      {m?.totalCents != null
                        ? formatCents(m.totalCents)
                        : ri.expense
                          ? formatCents(ri.expense.totalCents)
                          : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {ri.expense ? (
                        <ExpenseStatusBadge status={ri.expense.status} code={ri.expense.code} />
                      ) : (
                        <span className="text-[10px] t-faint">{t('ocr.list.expStatus.SIN_GASTO')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {ri.hasOcr ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-jr2-500/15 text-jr2-700 dark:text-jr2-300">
                          <ScanText size={10} /> {t('ocr.list.source.ocr')}
                        </span>
                      ) : (
                        <span className="text-[10px] t-faint">{t('ocr.list.source.manual')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {ri.hasOcr && (
                          <button
                            type="button"
                            onClick={() => setViewer(ri)}
                            title={t('ocr.list.viewOcr')}
                            className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                          >
                            <ScanEye size={14} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void onDownload(ri)}
                          disabled={!ri.latestVersion}
                          title={t('ocr.list.viewDoc')}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200 disabled:opacity-40"
                        >
                          <Download size={14} />
                        </button>
                        {/* Corregir lo que leyó la IA. Se permite mientras el
                            gasto no esté pagado: a partir de ahí el asiento y la
                            deuda con el proveedor ya están en la contabilidad. */}
                        {canWrite && ri.expense?.status !== 'PAGADO' && (
                          <button
                            type="button"
                            onClick={() => setEditDoc(ri)}
                            title={t('ocr.list.editMeta')}
                            className="w-7 h-7 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => void onDelete(ri)}
                            title={t('ocr.list.deleteDoc')}
                            className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {total > 0 && (
        <div className="flex items-center justify-between text-xs t-muted">
          <span>{t('ocr.list.pageInfo', { from, to, total })}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => updateParam('page', String(page - 1))}
              disabled={page <= 1}
              className="w-7 h-7 grid place-items-center rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              onClick={() => updateParam('page', String(page + 1))}
              disabled={to >= total}
              className="w-7 h-7 grid place-items-center rounded border border-base hover:bg-hover disabled:opacity-40"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {uploadOpen && (
        <UploadInvoiceModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false)
            void load()
          }}
        />
      )}

      {editDoc && (
        <UploadInvoiceModal
          editing={editDoc}
          onClose={() => setEditDoc(null)}
          onUploaded={() => {
            setEditDoc(null)
            void load()
          }}
        />
      )}

      {viewer && (
        <OcrViewerModal item={viewer} onClose={() => setViewer(null)} onDownload={onDownload} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <label className="text-xs t-muted flex items-center gap-2">
      <Filter size={12} className="t-faint" /> {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  )
}

function ExpenseStatusBadge({
  status,
  code,
}: {
  status: 'PENDIENTE' | 'PAGADO' | 'ANULADO'
  code: string
}) {
  const { t } = useTranslation('documents')
  const STYLES: Record<string, string> = {
    PENDIENTE: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    PAGADO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    ANULADO: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`px-1.5 py-0.5 rounded text-[10px] ${STYLES[status] ?? ''}`}>
        {t(`ocr.list.expStatus.${status}`)}
      </span>
      <span className="text-[10px] t-faint font-mono">#{code}</span>
    </span>
  )
}

// Visor de lo que la OCR/IA interpretó de una factura recibida. Solo lectura.
function OcrViewerModal({
  item,
  onClose,
  onDownload,
}: {
  item: ReceivedInvoiceItem
  onClose: () => void
  onDownload: (item: ReceivedInvoiceItem) => void
}) {
  const { t } = useTranslation(['documents', 'common'])
  const m = item.invoiceMeta
  const [text, setText] = useState<string | null>(null)
  const [showText, setShowText] = useState(false)
  const [loadingText, setLoadingText] = useState(false)

  const loadText = async () => {
    if (text !== null) {
      setShowText((s) => !s)
      return
    }
    setLoadingText(true)
    try {
      const res = await api.get<DocumentExtractedResponse>(`/documents/${item.id}/extracted`)
      setText(res.text ?? '')
      setShowText(true)
    } catch {
      setText('')
      setShowText(true)
    } finally {
      setLoadingText(false)
    }
  }

  const money = (c: number | null | undefined) => (c == null ? '—' : formatCents(c))
  const pct = (bps: number | null | undefined) => (bps == null ? '—' : `${(bps / 100).toString().replace('.', ',')}%`)
  const dateStr = (s: string | null | undefined) =>
    s ? new Date(s).toLocaleDateString(currentLocaleTag()) : '—'

  const fields: { label: string; value: string; strong?: boolean }[] = [
    { label: t('ocr.fields.supplier'), value: m?.supplier ?? '—' },
    { label: t('ocr.fields.cif'), value: m?.cif ?? '—' },
    { label: t('ocr.fields.invoiceNumber'), value: m?.invoiceNumber ?? '—' },
    { label: t('ocr.fields.issueDate'), value: dateStr(m?.issueDate) },
    { label: t('ocr.fields.currency'), value: m?.currency ?? '—' },
    { label: t('ocr.fields.baseCents'), value: money(m?.baseCents) },
    { label: t('ocr.fields.vatRateBps'), value: pct(m?.vatRateBps) },
    { label: t('ocr.fields.vatCents'), value: money(m?.vatCents) },
    { label: t('ocr.fields.irpfRateBps'), value: pct(m?.irpfRateBps) },
    { label: t('ocr.fields.irpfCents'), value: money(m?.irpfCents) },
  ]

  return createPortal(
    <div className="absolute inset-0 z-[100] flex flex-col" style={{ background: 'var(--bg-page)' }}>
      <div
        className="flex-shrink-0 px-6 py-4 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ScanEye size={20} className="text-jr2-400 shrink-0" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold t-primary truncate">{t('ocr.viewer.title')}</h3>
            <p className="text-xs t-muted truncate">{item.fileName}</p>
          </div>
          {m?.confidence != null && (
            <span
              className={[
                'ml-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider shrink-0',
                m.confidence >= 80
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200'
                  : m.confidence >= 50
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-200'
                    : 'bg-rose-500/15 text-rose-700 dark:text-rose-200',
              ].join(' ')}
            >
              {t('ocr.uploadModal.confidence', { value: m.confidence })}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 grid place-items-center rounded-lg t-muted hover:t-primary hover:bg-hover"
          aria-label={t('common:actions.close')}
        >
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-3xl mx-auto space-y-4">
          <p className="text-sm t-muted flex items-center gap-2">
            <Sparkles size={14} className="text-jr2-400 shrink-0" />
            {t('ocr.viewer.subtitle')}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {fields.map((f, i) => (
              <div key={i} className="rounded-lg border border-base px-3 py-2" style={{ background: 'var(--bg-input-soft)' }}>
                <div className="text-[10px] uppercase tracking-wider t-faint">{f.label}</div>
                <div className="text-sm t-primary mt-0.5 font-mono truncate" title={f.value}>{f.value}</div>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-jr2-500/30 bg-jr2-500/[0.06] px-3 py-2 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider t-secondary">{t('ocr.fields.totalCents')}</span>
            <span className="text-lg font-semibold font-mono t-primary">{money(m?.totalCents)}</span>
          </div>

          {/* Texto extraído (bajo demanda) */}
          <div>
            <button
              type="button"
              onClick={() => void loadText()}
              className="text-xs t-secondary hover:t-primary underline flex items-center gap-1.5"
            >
              {loadingText ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
              {showText ? t('ocr.viewer.hideText') : t('ocr.viewer.showText')}
            </button>
            {showText && (
              <pre
                className="mt-2 text-xs whitespace-pre-wrap leading-relaxed t-secondary font-sans border border-base rounded-lg p-3 max-h-[40vh] overflow-y-auto"
                style={{ background: 'var(--bg-input-soft)' }}
              >
                {text || <span className="t-faint">{t('ocr.viewer.noText')}</span>}
              </pre>
            )}
          </div>
        </div>
      </div>

      <div
        className="flex-shrink-0 px-6 py-3 border-t flex justify-end gap-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
        >
          {t('common:actions.close')}
        </button>
        <button
          type="button"
          onClick={() => onDownload(item)}
          disabled={!item.latestVersion}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
        >
          <Download size={14} /> {t('ocr.viewer.viewDoc')}
        </button>
      </div>
    </div>,
    getDialogHost()
  )
}

// ---------------------------------------------------------------------------

type MetaKey =
  | 'supplier'
  | 'cif'
  | 'invoiceNumber'
  | 'issueDate'
  | 'currency'
  | 'baseCents'
  | 'vatRateBps'
  | 'vatCents'
  | 'irpfRateBps'
  | 'irpfCents'
  | 'totalCents'

function UploadInvoiceModal({
  onClose,
  onUploaded,
  editing,
}: {
  onClose: () => void
  onUploaded: (docId: string) => void
  /**
   * Factura YA subida cuyos campos se quieren corregir. Con esto el modal se
   * salta el paso del fichero y arranca directamente en el formulario, relleno
   * con lo que interpretó la IA. Guarda contra el MISMO endpoint que la
   * confirmación inicial, que ya sabía actualizar un gasto pendiente.
   */
  editing?: ReceivedInvoiceItem | null
}) {
  const { t } = useTranslation(['documents', 'common'])
  const [file, setFile] = useState<File | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [result, setResult] = useState<AnalyzeInvoiceResponse | null>(
    editing
      ? {
          document: { id: editing.id, fileName: editing.fileName },
          extractedText: '',
          invoiceMeta: editing.invoiceMeta as InvoiceMeta,
          aiError: null,
          duplicateOf: null,
        }
      : null
  )
  const [meta, setMeta] = useState<InvoiceMeta | null>(
    editing ? ((editing.invoiceMeta ?? null) as InvoiceMeta | null) : null
  )
  const inputRef = useRef<HTMLInputElement>(null)

  const onAnalyze = async () => {
    if (!file) {
      setErr(t('ocr.uploadModal.errFile'))
      return
    }
    setAnalyzing(true)
    setErr(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const resp = await api.upload<AnalyzeInvoiceResponse>(
        '/documents/analyze-invoice',
        form,
      )
      setResult(resp)
      setMeta(resp.invoiceMeta)
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setAnalyzing(false)
    }
  }

  const onSave = async () => {
    if (!result || !meta) return
    setSaving(true)
    setErr(null)
    try {
      await api.patch(`/documents/${result.document.id}/invoice-meta`, meta)
      onUploaded(result.document.id)
    } catch (e) {
      setErr(extractErr(e))
    } finally {
      setSaving(false)
    }
  }

  const setMetaField = <K extends keyof InvoiceMeta>(k: K, v: InvoiceMeta[K]) => {
    setMeta((prev) => (prev ? { ...prev, [k]: v } : prev))
  }

  const setTextField = (k: MetaKey, raw: string) => {
    setMetaField(k as keyof InvoiceMeta, (raw.trim() === '' ? null : raw) as never)
  }

  const setCentsField = (k: 'baseCents' | 'vatCents' | 'irpfCents' | 'totalCents', raw: string) => {
    if (raw.trim() === '') return setMetaField(k, null)
    const cents = parseEurosToCents(raw)
    setMetaField(k, cents)
  }

  const setBpsField = (k: 'vatRateBps' | 'irpfRateBps', raw: string) => {
    if (raw.trim() === '') return setMetaField(k, null)
    const asNum = Number(raw.replace(',', '.'))
    if (!Number.isFinite(asNum)) return
    setMetaField(k, Math.round(asNum * 100))
  }

  const bpsToPctInput = (bps: number | null): string =>
    bps == null ? '' : (bps / 100).toString().replace('.', ',')

  const inputCls =
    'w-full px-2.5 py-1.5 rounded-md bg-[var(--bg-input)] border border-base text-sm t-primary'

  const field = (label: string, control: React.ReactNode, spanClass = '') => (
    <div className={spanClass}>
      <label className="block text-[11px] t-secondary mb-0.5 leading-tight">{label}</label>
      {control}
    </div>
  )

  // F41 — Diálogo montado dentro del <main> (id="app-main") vía portal, con
  // `absolute inset-0` para cubrir SOLO el área de contenido de la app,
  // dejando sidebar y topbar visibles. El portal evita que ancestros con
  // `transform` (animate-slideUp) rompan el posicionamiento — como <main>
  // tiene `position: relative`, el absolute se ancla ahí.
  return createPortal(
    <div
      className="absolute inset-0 z-[100] flex flex-col"
      style={{ background: 'var(--bg-page)' }}
    >
      {/* Cabecera */}
      <div
        className="flex-shrink-0 px-6 py-4 border-b flex items-center justify-between"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ScanText size={20} className="text-jr2-400 shrink-0" />
          <h3 className="text-base font-semibold t-primary truncate">
            {editing ? t('ocr.uploadModal.editTitle') : t('ocr.uploadModal.title')}
          </h3>
          {meta && meta.confidence != null && (
            <span
              className={[
                'ml-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider shrink-0',
                meta.confidence >= 80
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200'
                  : meta.confidence >= 50
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-200'
                    : 'bg-rose-500/15 text-rose-700 dark:text-rose-200',
              ].join(' ')}
            >
              {t('ocr.uploadModal.confidence', { value: meta.confidence })}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 grid place-items-center rounded-lg t-muted hover:t-primary hover:bg-hover"
          aria-label={t('common:actions.close')}
        >
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-3xl mx-auto">
          {!meta && (
            <div className="space-y-4">
              <p className="text-sm t-muted">{t('ocr.uploadModal.subtitle')}</p>
              <div className="border-2 border-dashed border-base rounded-xl p-10 text-center">
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                  id="ocr-upload-file"
                />
                <label htmlFor="ocr-upload-file" className="cursor-pointer">
                  <ScanText size={40} className="text-jr2-400 mx-auto mb-3" />
                  <p className="text-base t-primary font-medium">
                    {file ? file.name : t('ocr.uploadModal.dropHint')}
                  </p>
                  <p className="text-xs t-muted mt-1">
                    {file
                      ? `${Math.round(file.size / 1024)} KB · ${t('ocr.uploadModal.chooseAnother')}`
                      : t('ocr.uploadModal.dropHintExt')}
                  </p>
                </label>
              </div>
              {err && (
                <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}
            </div>
          )}

          {meta && (
            <div className="space-y-4">
              {/* Si la IA falló, decirlo AQUÍ y no dejar el formulario en blanco
                  sin explicación: eso llevó a subir la misma factura dos veces
                  creyendo que no se había guardado. */}
              {result?.duplicateOf && (
                <div className="text-sm rounded-lg border px-3 py-2 flex items-start gap-2 text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40">
                  <AlertCircle size={15} className="shrink-0 mt-0.5" />
                  <span>
                    {t('ocr.uploadModal.duplicate', {
                      fileName: result.duplicateOf.fileName,
                      date: new Date(result.duplicateOf.createdAt).toLocaleString(
                        currentLocaleTag()
                      ),
                    })}
                  </span>
                </div>
              )}
              {result?.aiError ? (
                <div className="text-sm rounded-lg border px-3 py-2 flex items-start gap-2 text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40">
                  <AlertCircle size={15} className="shrink-0 mt-0.5" />
                  <span>
                    <strong>{t('ocr.uploadModal.aiFailedTitle')}</strong> {result.aiError}{' '}
                    {t('ocr.uploadModal.aiFailedHint')}
                  </span>
                </div>
              ) : (
                <p className="text-sm t-muted flex items-center gap-2">
                  <Sparkles size={14} className="text-jr2-400 shrink-0" />
                  {t('ocr.uploadModal.reviewSubtitle')}
                </p>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {field(
                  t('ocr.fields.supplier'),
                  <input
                    type="text"
                    value={meta.supplier ?? ''}
                    onChange={(e) => setTextField('supplier', e.target.value)}
                    className={inputCls}
                  />,
                  'sm:col-span-3',
                )}
                {field(
                  t('ocr.fields.cif'),
                  <input
                    type="text"
                    value={meta.cif ?? ''}
                    onChange={(e) => setTextField('cif', e.target.value)}
                    className={inputCls}
                  />,
                )}
                {field(
                  t('ocr.fields.invoiceNumber'),
                  <input
                    type="text"
                    value={meta.invoiceNumber ?? ''}
                    onChange={(e) => setTextField('invoiceNumber', e.target.value)}
                    className={inputCls}
                  />,
                )}
                {field(
                  t('ocr.fields.issueDate'),
                  <input
                    type="date"
                    value={meta.issueDate ?? ''}
                    onChange={(e) => setTextField('issueDate', e.target.value)}
                    className={inputCls}
                  />,
                )}
                {field(
                  t('ocr.fields.baseCents'),
                  <input
                    type="text"
                    inputMode="decimal"
                    value={meta.baseCents == null ? '' : centsToEurosInput(meta.baseCents)}
                    onChange={(e) => setCentsField('baseCents', e.target.value)}
                    placeholder="0,00"
                    className={`${inputCls} font-mono text-right`}
                  />,
                )}
                {field(
                  t('ocr.fields.vatRateBps'),
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bpsToPctInput(meta.vatRateBps)}
                    onChange={(e) => setBpsField('vatRateBps', e.target.value)}
                    placeholder="21"
                    className={`${inputCls} font-mono text-right`}
                  />,
                )}
                {field(
                  t('ocr.fields.vatCents'),
                  <input
                    type="text"
                    inputMode="decimal"
                    value={meta.vatCents == null ? '' : centsToEurosInput(meta.vatCents)}
                    onChange={(e) => setCentsField('vatCents', e.target.value)}
                    placeholder="0,00"
                    className={`${inputCls} font-mono text-right`}
                  />,
                )}
                {field(
                  t('ocr.fields.irpfRateBps'),
                  <input
                    type="text"
                    inputMode="decimal"
                    value={bpsToPctInput(meta.irpfRateBps)}
                    onChange={(e) => setBpsField('irpfRateBps', e.target.value)}
                    placeholder="15"
                    className={`${inputCls} font-mono text-right`}
                  />,
                )}
                {field(
                  t('ocr.fields.irpfCents'),
                  <input
                    type="text"
                    inputMode="decimal"
                    value={meta.irpfCents == null ? '' : centsToEurosInput(meta.irpfCents)}
                    onChange={(e) => setCentsField('irpfCents', e.target.value)}
                    placeholder="0,00"
                    className={`${inputCls} font-mono text-right`}
                  />,
                )}
                {field(
                  t('ocr.fields.currency'),
                  <input
                    type="text"
                    value={meta.currency ?? ''}
                    onChange={(e) => setTextField('currency', e.target.value)}
                    placeholder="EUR"
                    className={inputCls}
                  />,
                )}
                <div
                  className="sm:col-span-3 border-t pt-3 mt-1"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <label className="block text-xs t-secondary mb-1 font-semibold">
                    {t('ocr.fields.totalCents')}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={meta.totalCents == null ? '' : centsToEurosInput(meta.totalCents)}
                    onChange={(e) => setCentsField('totalCents', e.target.value)}
                    placeholder="0,00"
                    className={`${inputCls} font-mono text-right text-lg font-semibold py-2`}
                  />
                </div>
              </div>

              {err && (
                <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pie sticky con botones */}
      <div
        className="flex-shrink-0 px-6 py-3 border-t flex justify-end gap-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={analyzing || saving}
          className="px-4 py-2 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
        >
          {t('common:actions.cancel')}
        </button>
        {!meta ? (
          <button
            type="button"
            onClick={() => void onAnalyze()}
            disabled={analyzing || !file}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {analyzing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Sparkles size={14} />
            )}
            {analyzing ? t('ocr.uploadModal.analyzing') : t('ocr.uploadModal.analyze')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {t('ocr.uploadModal.confirmSave')}
          </button>
        )}
      </div>
    </div>,
    getDialogHost(),
  )
}

function extractErr(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    return body?.error ?? err.message
  }
  return err instanceof Error ? err.message : 'Error inesperado'
}
